import { Component, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { checkServiceLocation, ServiceAreaCheckResult } from '../../shared/utils/service-area';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [RouterLink, FormsModule],
  template: `
    <!-- Hero Section -->
    <section class="pt-32 pb-20 px-8 lg:px-32 bg-white flex flex-col lg:flex-row items-center gap-16 min-h-screen">
      <div class="flex-1 space-y-8">
        <div class="inline-flex items-center gap-2 px-4 py-2 border-2 border-gold-accent rounded-full">
          <span class="material-symbols-outlined text-tertiary" style="font-variation-settings: 'FILL' 1;">verified</span>
          <span class="font-sans font-bold text-sm text-tertiary tracking-widest uppercase">ESTABLISHED TRUST</span>
        </div>
        <h1 class="font-serif text-5xl lg:text-[64px] lg:leading-[72px] text-on-surface">
          The ultimate residential <br/>cleaning experience.
        </h1>
        <p class="font-sans text-xl text-on-surface-variant max-w-xl">
          Experience a level of care that transcends traditional cleaning. Our certified professionals are trained in luxury etiquette, medical first aid, and the meticulous preservation of your sanctuary.
        </p>
        <div class="flex flex-col sm:flex-row gap-6 pt-4">
          <button class="bg-gold-accent text-black font-bold h-14 px-12 rounded-lg shadow-lg hover:brightness-105 active:scale-95 transition-all" routerLink="/book">
            Book Now
          </button>
          <button class="border-2 border-on-surface text-on-surface font-bold h-14 px-12 rounded-lg hover:bg-stone-50 transition-all" routerLink="/services">
            View Services
          </button>
        </div>
      </div>
      <div class="flex-1 relative w-full aspect-[4/5] max-w-2xl">
        <div class="absolute inset-0 bg-stone-100 rounded-xl overflow-hidden shadow-2xl border border-stone-100">
          <img class="w-full h-full object-cover" src="/assets/img/cleaning-service-interior.png" alt="Professional cleaning service interior">
        </div>
        <!-- Trust Badge Overlay -->
        <div class="absolute -bottom-10 -left-10 bg-white p-8 rounded-xl shadow-xl border border-stone-100 hidden xl:block max-w-[280px]">
          <p class="font-serif text-lg text-on-surface mb-4 italic">"Impeccable service that honors the sanctity of the home."</p>
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-stone-200"></div>
            <div>
              <p class="font-sans font-bold text-sm text-on-surface">Lisa L.</p>
              <p class="text-xs text-on-surface-variant">Regency House Client</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Certification Bento Grid -->
    <section class="py-20 px-8 lg:px-32 bg-stone-50">
      <div class="text-center mb-16">
        <h2 class="font-serif text-4xl text-on-surface mb-4">Certified Excellence</h2>
        <p class="font-sans text-lg text-on-surface-variant max-w-2xl mx-auto">
          You can rest easy knowing our deeply vetted specialists treat your home with the utmost respect and care.
        </p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- CPR & First Aid -->
        <div class="bg-white p-12 border border-stone-200 rounded-xl flex flex-col items-start gap-6 hover:shadow-md transition-shadow">
          <div class="w-16 h-16 rounded-full bg-stone-50 flex items-center justify-center">
            <span class="material-symbols-outlined text-4xl text-gold-accent">medical_services</span>
          </div>
          <h3 class="font-serif text-2xl">Safety First</h3>
          <p class="text-on-surface-variant">Every specialist is certified CPR and First Aid certified, ensuring peace of mind for you and your family.</p>
          <div class="mt-auto pt-4 border-t border-stone-100 w-full">
            <span class="font-sans font-bold text-xs text-tertiary tracking-widest uppercase">OSHA COMPLIANT</span>
          </div>
        </div>
        <!-- Etiquette -->
        <div class="bg-white p-12 border border-stone-200 rounded-xl flex flex-col items-start gap-6 shadow-xl relative md:-translate-y-8">
          <div class="w-16 h-16 rounded-full bg-stone-50 flex items-center justify-center">
            <span class="material-symbols-outlined text-4xl text-gold-accent">social_leaderboard</span>
          </div>
          <h3 class="font-serif text-2xl">Refined Etiquette</h3>
          <p class="text-on-surface-variant">Trained in the highest standards of professional conduct, ensuring discretion and presence that complements your lifestyle.</p>
          <div class="mt-auto pt-4 border-t border-stone-100 w-full">
            <span class="font-sans font-bold text-xs text-tertiary tracking-widest uppercase">CERTIFIED  SPECIALIST</span>
          </div>
        </div>
        <!-- Material Care -->
        <div class="bg-white p-12 border border-stone-200 rounded-xl flex flex-col items-start gap-6 hover:shadow-md transition-shadow">
          <div class="w-16 h-16 rounded-full bg-stone-50 flex items-center justify-center">
            <span class="material-symbols-outlined text-4xl text-gold-accent">diamond</span>
          </div>
          <h3 class="font-serif text-2xl">Heritage Preservation</h3>
          <p class="text-on-surface-variant">Specialized training in the care of precious materials: marble, fine linens, hardwoods, and delicate antiques.</p>
          <div class="mt-auto pt-4 border-t border-stone-100 w-full">
            <span class="font-sans font-bold text-xs text-tertiary tracking-widest uppercase">MATERIAL EXPERTS</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Operating Area: Exclusively New York City -->
    <section id="service-area" class="py-24 px-8 lg:px-32 bg-stone-900 text-stone-100 relative overflow-hidden border-t border-stone-800">
      <div class="max-w-7xl mx-auto space-y-16">

        <!-- Section Header -->
        <div class="text-center max-w-3xl mx-auto space-y-4">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30">
            <span class="material-symbols-outlined text-amber-400 text-base" style="font-variation-settings: 'FILL' 1;">location_on</span>
            <span class="font-sans font-bold text-xs text-amber-400 tracking-widest uppercase">EXCLUSIVELY SERVING NEW YORK CITY</span>
          </div>
          <h2 class="font-serif text-4xl lg:text-5xl text-white">
            Our Operating Area: Exclusively New York City
          </h2>
          <div class="w-24 h-1 bg-gold-accent mx-auto my-3"></div>
          <p class="font-sans text-lg text-stone-300 leading-relaxed pt-2">
            Regency House Cleaning operates exclusively within <strong>New York City</strong>. By focusing all our resources, equipment, and certified specialists strictly across <strong>Manhattan</strong>, <strong>Brooklyn</strong>, and <strong>Queens</strong>, we guarantee punctual arrivals, seamless doorman coordination, and the highest standard of quiet luxury care.
          </p>
        </div>

        <!-- Interactive NYC ZIP & Neighborhood Checker -->
        <div class="max-w-3xl mx-auto bg-stone-800/80 border border-stone-700/80 rounded-2xl p-6 sm:p-8 shadow-2xl backdrop-blur-sm">
          <div class="flex items-center justify-between gap-4 mb-4">
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-gold-accent text-xl">travel_explore</span>
              <h3 class="font-serif text-lg sm:text-xl font-bold text-white">Verify Your Building or Neighborhood</h3>
            </div>
            <span class="text-xs text-stone-400 font-mono hidden sm:inline">NYC Service Area</span>
          </div>

          <div class="flex flex-col sm:flex-row gap-3">
            <div class="relative flex-1">
              <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">search</span>
              <input
                type="text"
                [(ngModel)]="searchQuery"
                (input)="onSearchChange()"
                placeholder="Enter 5-digit ZIP (e.g. 10021, 11201, 11101) or neighborhood..."
                class="w-full h-14 pl-12 pr-4 bg-stone-900 border border-stone-700 rounded-xl text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 text-sm font-sans"
              />
            </div>
            <button
              type="button"
              (click)="onSearchChange()"
              class="h-14 px-8 bg-gold-accent hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
            >
              <span>Check Availability</span>
              <span class="material-symbols-outlined text-base">check</span>
            </button>
          </div>

          <!-- Quick Borough Filter Buttons -->
          <div class="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-stone-700/60 text-xs">
            <span class="text-stone-400">Quick explore:</span>
            <button
              type="button"
              (click)="selectBorough('Manhattan')"
              class="px-3 py-1 rounded-full bg-stone-700/50 hover:bg-amber-500/20 hover:text-amber-300 text-stone-300 border border-stone-600 transition-colors cursor-pointer"
            >
              Manhattan
            </button>
            <button
              type="button"
              (click)="selectBorough('Brooklyn')"
              class="px-3 py-1 rounded-full bg-stone-700/50 hover:bg-amber-500/20 hover:text-amber-300 text-stone-300 border border-stone-600 transition-colors cursor-pointer"
            >
              Brooklyn
            </button>
            <button
              type="button"
              (click)="selectBorough('Queens')"
              class="px-3 py-1 rounded-full bg-stone-700/50 hover:bg-amber-500/20 hover:text-amber-300 text-stone-300 border border-stone-600 transition-colors cursor-pointer"
            >
              Queens
            </button>
          </div>

          <!-- Result Feedback Card -->
          @if (checkResult().status === 'serviceable') {
            <div class="mt-5 p-4 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-in fade-in">
              <div class="flex items-start gap-3">
                <span class="material-symbols-outlined text-emerald-400 text-2xl shrink-0 mt-0.5" style="font-variation-settings: 'FILL' 1;">check_circle</span>
                <div>
                  <h4 class="font-bold text-sm text-emerald-300">{{ checkResult().title }}</h4>
                  <p class="text-xs text-stone-300 leading-relaxed mt-0.5">{{ checkResult().message }}</p>
                </div>
              </div>
              <a
                routerLink="/book"
                class="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-gold-accent hover:bg-amber-400 text-stone-950 text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm transition-all shrink-0 cursor-pointer"
              >
                <span>Schedule Now</span>
                <span class="material-symbols-outlined text-sm">arrow_forward</span>
              </a>
            </div>
          } @else if (checkResult().status === 'outside') {
            <div class="mt-5 p-4 rounded-xl bg-amber-950/50 border border-amber-500/40 text-amber-200 flex items-start gap-3 animate-in fade-in">
              <span class="material-symbols-outlined text-amber-400 text-2xl shrink-0 mt-0.5">location_off</span>
              <div>
                <h4 class="font-bold text-sm text-amber-300">{{ checkResult().title }}</h4>
                <p class="text-xs text-stone-300 leading-relaxed mt-0.5">{{ checkResult().message }}</p>
                <p class="text-xs text-amber-400/90 mt-2 font-medium">
                  Have a question about your building location? Call our NYC team at <a href="tel:6463053661" class="underline font-bold hover:text-amber-300">(646) 305-3661</a>.
                </p>
              </div>
            </div>
          }
        </div>

        <!-- 3 Borough Highlights Cards -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <!-- Manhattan Card -->
          <div class="bg-stone-800/60 border border-stone-700 rounded-2xl p-8 flex flex-col justify-between hover:border-amber-400/50 transition-all group">
            <div class="space-y-5">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-gold-accent">
                  <span class="material-symbols-outlined text-3xl">domain</span>
                </div>
                <span class="px-3 py-1 rounded-full bg-stone-700/60 text-[11px] font-bold text-amber-400 uppercase tracking-widest border border-stone-600">
                  All Neighborhoods
                </span>
              </div>
              
              <div>
                <h3 class="font-serif text-3xl text-white group-hover:text-amber-400 transition-colors">Manhattan</h3>
                <p class="text-xs text-amber-400/90 font-medium uppercase tracking-wider mt-1">High-Rises, Co-ops &amp; Historic Townhomes</p>
              </div>

              <p class="text-sm text-stone-300 leading-relaxed font-light">
                Comprehensive luxury cleaning for pre-war residences, penthouses, and landmark buildings. Full concierge and management coordination.
              </p>

              <div class="space-y-2 pt-3 border-t border-stone-700/60">
                <span class="text-xs font-semibold text-stone-400 uppercase tracking-wider block">Featured Neighborhoods:</span>
                <div class="flex flex-wrap gap-1.5 text-xs text-stone-300">
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Upper East Side</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Upper West Side</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Tribeca</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">SoHo</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">West Village</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Midtown</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Chelsea</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Financial District</span>
                </div>
              </div>
            </div>

            <div class="mt-8 pt-4 border-t border-stone-700/40 flex items-center gap-2 text-xs text-stone-400">
              <span class="material-symbols-outlined text-amber-400 text-base">verified</span>
              <span>Building COI &amp; Doorman Protocol Ready</span>
            </div>
          </div>

          <!-- Brooklyn Card -->
          <div class="bg-stone-800/60 border border-stone-700 rounded-2xl p-8 flex flex-col justify-between hover:border-amber-400/50 transition-all group">
            <div class="space-y-5">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-gold-accent">
                  <span class="material-symbols-outlined text-3xl">home_pin</span>
                </div>
                <span class="px-3 py-1 rounded-full bg-stone-700/60 text-[11px] font-bold text-amber-400 uppercase tracking-widest border border-stone-600">
                  Historic &amp; Waterfront
                </span>
              </div>
              
              <div>
                <h3 class="font-serif text-3xl text-white group-hover:text-amber-400 transition-colors">Brooklyn</h3>
                <p class="text-xs text-amber-400/90 font-medium uppercase tracking-wider mt-1">Brownstones, Townhouses &amp; Lofts</p>
              </div>

              <p class="text-sm text-stone-300 leading-relaxed font-light">
                Tailored architectural care for historic woodwork, delicate plaster, exposed brick, and modern waterfront condominium interiors.
              </p>

              <div class="space-y-2 pt-3 border-t border-stone-700/60">
                <span class="text-xs font-semibold text-stone-400 uppercase tracking-wider block">Featured Neighborhoods:</span>
                <div class="flex flex-wrap gap-1.5 text-xs text-stone-300">
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Brooklyn Heights</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">DUMBO</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Park Slope</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Cobble Hill</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Carroll Gardens</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Williamsburg</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Greenpoint</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Fort Greene</span>
                </div>
              </div>
            </div>

            <div class="mt-8 pt-4 border-t border-stone-700/40 flex items-center gap-2 text-xs text-stone-400">
              <span class="material-symbols-outlined text-amber-400 text-base">verified</span>
              <span>Heritage Hardwood &amp; Stone Preservation</span>
            </div>
          </div>

          <!-- Queens Card -->
          <div class="bg-stone-800/60 border border-stone-700 rounded-2xl p-8 flex flex-col justify-between hover:border-amber-400/50 transition-all group">
            <div class="space-y-5">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-gold-accent">
                  <span class="material-symbols-outlined text-3xl">holiday_village</span>
                </div>
                <span class="px-3 py-1 rounded-full bg-stone-700/60 text-[11px] font-bold text-amber-400 uppercase tracking-widest border border-stone-600">
                  Waterfront &amp; Enclaves
                </span>
              </div>
              
              <div>
                <h3 class="font-serif text-3xl text-white group-hover:text-amber-400 transition-colors">Queens</h3>
                <p class="text-xs text-amber-400/90 font-medium uppercase tracking-wider mt-1">Luxury Developments &amp; Enclaves</p>
              </div>

              <p class="text-sm text-stone-300 leading-relaxed font-light">
                Elevated residential care for modern high-rises along the East River waterfront and stately private homes in quiet enclave neighborhoods.
              </p>

              <div class="space-y-2 pt-3 border-t border-stone-700/60">
                <span class="text-xs font-semibold text-stone-400 uppercase tracking-wider block">Featured Neighborhoods:</span>
                <div class="flex flex-wrap gap-1.5 text-xs text-stone-300">
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Long Island City</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Astoria</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Forest Hills</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Forest Hills Gardens</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Sunnyside</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Bayside</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Kew Gardens</span>
                  <span class="bg-stone-900/80 px-2.5 py-1 rounded border border-stone-700">Woodside</span>
                </div>
              </div>
            </div>

            <div class="mt-8 pt-4 border-t border-stone-700/40 flex items-center gap-2 text-xs text-stone-400">
              <span class="material-symbols-outlined text-amber-400 text-base">verified</span>
              <span>Hospital-Grade HEPA Sanitization</span>
            </div>
          </div>

        </div>

        <!-- NYC White Glove Standards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-4">
          <div class="p-6 rounded-xl bg-stone-800/40 border border-stone-800 space-y-3">
            <span class="material-symbols-outlined text-amber-400 text-3xl">description</span>
            <h4 class="font-serif text-lg font-bold text-white">NYC COI Compliant</h4>
            <p class="text-xs text-stone-400 leading-relaxed">
              We provide formal Certificates of Insurance naming your building’s management and board prior to every visit.
            </p>
          </div>

          <div class="p-6 rounded-xl bg-stone-800/40 border border-stone-800 space-y-3">
            <span class="material-symbols-outlined text-amber-400 text-3xl">key</span>
            <h4 class="font-serif text-lg font-bold text-white">Concierge &amp; Key Custody</h4>
            <p class="text-xs text-stone-400 leading-relaxed">
              Seamless key pick-up and drop-off with your 24/7 lobby concierge or secure lockbox so your day is uninterrupted.
            </p>
          </div>

          <div class="p-6 rounded-xl bg-stone-800/40 border border-stone-800 space-y-3">
            <span class="material-symbols-outlined text-amber-400 text-3xl">local_police</span>
            <h4 class="font-serif text-lg font-bold text-white">Strict Discretion</h4>
            <p class="text-xs text-stone-400 leading-relaxed">
              Every steward signs binding confidentiality agreements, guaranteeing complete privacy for high-profile residents.
            </p>
          </div>

          <div class="p-6 rounded-xl bg-stone-800/40 border border-stone-800 space-y-3">
            <span class="material-symbols-outlined text-amber-400 text-3xl">schedule</span>
            <h4 class="font-serif text-lg font-bold text-white">Local NYC Stewards</h4>
            <p class="text-xs text-stone-400 leading-relaxed">
              Our specialists are stationed directly within the boroughs, eliminating out-of-city transit delays and cancellations.
            </p>
          </div>
        </div>

        <!-- Section CTA Footer Banner -->
        <div class="bg-stone-800/90 border border-stone-700/80 rounded-2xl p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
          <div class="space-y-2">
            <h3 class="font-serif text-2xl sm:text-3xl text-white">Ready for pristine care in your NYC residence?</h3>
            <p class="text-sm text-stone-300 max-w-xl">
              Book online in under 2 minutes or speak directly with our NYC residential concierge team.
            </p>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-4 shrink-0">
            <a
              routerLink="/book"
              class="w-full sm:w-auto h-14 px-8 bg-gold-accent hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all cursor-pointer"
            >
              <span>Schedule Initial Visit</span>
              <span class="material-symbols-outlined text-base">calendar_month</span>
            </a>
            <a
              href="tel:6463053661"
              class="w-full sm:w-auto h-14 px-6 border border-stone-600 hover:border-gold-accent text-white hover:text-gold-accent font-bold uppercase tracking-wider text-xs rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <span class="material-symbols-outlined text-base">call</span>
              <span>(646) 305-3661</span>
            </a>
          </div>
        </div>

      </div>
    </section>
  `,
  styles: [`
    :host {
      display: block;
    }
  `],
})
export class HomeComponent {
  searchQuery = '';
  checkResult = signal<ServiceAreaCheckResult>({
    status: 'prompt',
    title: 'Check Your NYC Building or Neighborhood',
    message: 'Enter your 5-digit ZIP code or neighborhood to confirm immediate service availability.'
  });

  onSearchChange() {
    this.checkResult.set(checkServiceLocation(this.searchQuery));
  }

  selectBorough(name: string) {
    this.searchQuery = name;
    this.checkResult.set(checkServiceLocation(name));
  }
}
